import { useTranslation } from 'gatsby-plugin-react-i18next'
import { isEmpty } from 'lodash'
import React, { useEffect, useState } from 'react'
import { FaAngleRight } from 'react-icons/fa'
import Carousel from 'react-multi-carousel'

import useHomepage from '../../hooks/useHomepage'
import JustAdded from '../../images/homepage-justadded-image.png'
import PinImage from '../../images/location-pin-1.svg'
import UtmContent from '../../models/UtmContent'
import { AnalyticsService, UrlService } from '../../services'
import { MealType, RestaurantType } from '../../types'
import CarouselButtonGroup from '../common/CarouselButtonGroup'
import { placeholderMeal } from './helpers'

type Props = {
  restaurant: RestaurantType | undefined
  isSectionInView: boolean
}

type Slide = {
  src: string
  placeholder: boolean
  name: string
  description: string
  display_retail_price: string
  display_price: string
  display_percent_saved: string
}

const Restaurant: React.FC<Props> = ({ restaurant, isSectionInView }) => {
  const { t } = useTranslation()
  const { activeCityName, productOffering, showModal, launchConfirmationRequired, onNyLunchPage, landingPageAlias } =
    useHomepage()
  const [currentSlide, setCurrentSlide] = useState<Slide>(placeholderMeal)
  const [slides, setSlides] = useState<Slide[]>([placeholderMeal])
  const [key, setKey] = useState<number>(1)
  const [url, setUrl] = useState('')
  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 10000, min: 0 },
      items: 1,
    },
  }

  const [animation, setAnimation] = useState<string>('')

  useEffect(() => {
    updateActiveCity()
  }, [productOffering, onNyLunchPage])

  useEffect(() => {
    const animationSource = isSectionInView
      ? `https://images.mealpal.com/circle-transparent.gif?a=${Math.random()}`
      : ''
    setAnimation(animationSource)
  }, [isSectionInView])

  useEffect(() => {
    if (restaurant) {
      const newSlides = restaurant.meals.map((meal: MealType) => {
        return {
          src: meal.image_url,
          placeholder: false,
          name: meal.name,
          description: meal.description,
          display_retail_price: meal.display_retail_price,
          display_price: meal.display_price,
          display_percent_saved: meal.display_percent_saved,
        }
      })

      newSlides.push(placeholderMeal)
      setCurrentSlide(newSlides[0])
      setSlides(newSlides)
      setKey(key + 1)
    }
  }, [restaurant])

  const onClickHandler = (e: React.MouseEvent) => {
    if (launchConfirmationRequired) {
      e.preventDefault()

      return showModal({ name: 'get-access-form' })
    }

    updateActiveCity()

    AnalyticsService.trackEvent({
      action: 'Click',
      category: 'Homepage',
      label: 'Try Now - Map',
    })
  }

  const updateActiveCity = () => {
    const position =
      landingPageAlias !== '' ? `${landingPageAlias}-map` : productOffering === 'dinner' ? 'dinner-map' : 'map'
    const utmContent = new UtmContent({ position }).toString()
    setUrl(UrlService.signupUrl({ utmContent: utmContent, cityName: activeCityName }))
  }

  const afterChangeHandler = (prevSlide: number, props: { currentSlide: number }) => {
    const len = slides.length
    const index = (props.currentSlide - 2 + len) % len
    setCurrentSlide(slides[index])
  }

  return (
    <div className="restaurant">
      <div className="restaurant__info restaurant__name--lg">
        <div className={`restaurant__name ${!restaurant && 'blurred'}`}>{restaurant?.name}</div>
        <div className={`restaurant__address ${!restaurant && 'blurred'}`}>
          <img src={PinImage} />
          {restaurant?.address}
        </div>
      </div>
      <div className="restaurant__meal-wrapper">
        <div className="restaurant__carousel">
          <Carousel
            responsive={responsive}
            itemClass="restaurant__carousel-item"
            afterChange={afterChangeHandler}
            infinite={true}
            arrows={false}
            renderButtonGroupOutside={true}
            customButtonGroup={<CarouselButtonGroup />}
            key={key}
          >
            {slides.map((slide: Slide, index: number) => (
              <div key={index}>
                <img
                  className={`restaurant__image ${slide.placeholder ? 'restaurant__image--blur' : ''}`}
                  src={slide.src ? slide.src : JustAdded}
                />
                {!slide.src && !(slide.placeholder && !isEmpty(restaurant)) && (
                  <span className="restaurant__placeholder-text">{t('Sign up today to see more!')}</span>
                )}
                {slide.placeholder && !isEmpty(restaurant) && (
                  <span className="restaurant__placeholder-text">{t('Sign up today to view more meals!')}</span>
                )}
              </div>
            ))}
          </Carousel>
        </div>
        <div className="restaurant__meal-info">
          <div>
            <div className={`restaurant__name restaurant__name--xs ${!restaurant && 'blurred'}`}>
              {restaurant?.name}
            </div>
            <div className={`restaurant__meal ${currentSlide?.placeholder && 'blurred'}`}>{currentSlide?.name}</div>
            <div className={`restaurant__description ${currentSlide?.placeholder && 'blurred'}`}>
              <p>{currentSlide?.description}</p>
            </div>
          </div>
          <div>
            <div className={`restaurant__retail-price ${currentSlide?.placeholder && 'blurred'}`}>
              <div className={`restaurant__retail-price__savings`}>
                <span className="discount-percentage-label">
                  {t('SAVE ')}
                  {currentSlide?.display_percent_saved}
                  {t('%')}
                </span>
              </div>
              <div>
                {t('Retail Price:')}{' '}
                <span className="restaurant__retail-price__linethrough">{currentSlide?.display_retail_price}</span>
              </div>
            </div>
            <div
              className={`restaurant__price restaurant__price--${productOffering} ${
                currentSlide?.placeholder && 'blurred'
              }`}
            >
              {t('Your Price:')}
              <span className="restaurant__animation">
                {animation && !currentSlide?.placeholder && (
                  <div className={`restaurant__animation-image restaurant__animation-image--${productOffering}`}>
                    <img src={animation} />
                  </div>
                )}
                <span className="restaurant__display-price">{currentSlide?.display_price}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
      <a href={url} className="restaurant__action red-cta" onClick={onClickHandler}>
        {t('Try now')} <FaAngleRight />
      </a>
    </div>
  )
}

export default Restaurant
